"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import type { AnonymousFormat } from "@qrody/api-client/render";

interface UseActionHandlersOptions {
  /** Resolves the currently rendered QR blob (server mutation result or
   *  client-side preview). Returns null when nothing has rendered yet. */
  fetchCurrentBlob: () => Promise<Blob | null>;
  format: AnonymousFormat;
  /** CSS-mm size, used by the print pipeline to set image dimensions. */
  displayMm: number;
  size: number;
}

interface UseActionHandlersResult {
  toast: string | null;
  mounted: boolean;
  canShareFiles: boolean;
  onCopyImage: () => Promise<void>;
  onCopyHtml: () => Promise<void>;
  onShare: () => Promise<void>;
  onPrint: () => Promise<void>;
}

/**
 * Action-bar side-effects: toast lifecycle, hydration-safe mount gate,
 * navigator.canShare feature-detect, clipboard / share / print handlers.
 *
 * Returns plain functions (no useCallback) — the design-panel re-renders
 * on every state change anyway, memoising wouldn't change anything.
 */
export function useActionHandlers({
  fetchCurrentBlob,
  format,
  displayMm,
  size,
}: UseActionHandlersOptions): UseActionHandlersResult {
  const t = useTranslations("generator");

  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const handle = window.setTimeout(() => setToast(null), 2500);
    return () => window.clearTimeout(handle);
  }, [toast]);

  // Hydration-safe gate — Action-Bar needs browser APIs (clipboard,
  // navigator, print) and has no SEO value in SSR HTML.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: hydration-safe gate
    setMounted(true);
  }, []);

  // navigator.canShare with files is device-dependent; check post-hydration.
  const [canShareFiles, setCanShareFiles] = useState(false);
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.canShare) return;
    try {
      const probe = new File(["x"], "probe.png", { type: "image/png" });
      // eslint-disable-next-line react-hooks/set-state-in-effect -- feature-detection on mount
      setCanShareFiles(navigator.canShare({ files: [probe] }));
    } catch {
      setCanShareFiles(false);
    }
  }, []);

  const mimeForFormat = (f: AnonymousFormat): string =>
    f === "png" ? "image/png" : f === "svg" ? "image/svg+xml" : "application/pdf";

  const onCopyImage = async () => {
    const blob = await fetchCurrentBlob();
    if (!blob || typeof navigator === "undefined" || !navigator.clipboard?.write) {
      setToast(t("action.copyImageFailed"));
      return;
    }
    try {
      if (blob.type !== "image/png") {
        setToast(t("action.copyImageFailed"));
        return;
      }
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      setToast(t("action.copyImageDone"));
    } catch {
      setToast(t("action.copyImageFailed"));
    }
  };

  const onCopyHtml = async () => {
    const blob = await fetchCurrentBlob();
    if (!blob) {
      setToast(t("action.copyHtmlFailed"));
      return;
    }
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error("read failed"));
        reader.readAsDataURL(blob);
      });
      const html = `<img src="${dataUrl}" alt="qrody QR code" width="${size}" height="${size}" />`;
      await navigator.clipboard.writeText(html);
      setToast(t("action.copyHtmlDone"));
    } catch {
      setToast(t("action.copyHtmlFailed"));
    }
  };

  const onShare = async () => {
    const blob = await fetchCurrentBlob();
    if (!blob || typeof navigator === "undefined" || !navigator.share) {
      setToast(t("action.shareFailed"));
      return;
    }
    try {
      const ext = format === "pdf" ? "pdf" : format === "svg" ? "svg" : "png";
      const file = new File([blob], `qrody-qr.${ext}`, { type: mimeForFormat(format) });
      if (navigator.canShare && !navigator.canShare({ files: [file] })) {
        setToast(t("action.shareFailed"));
        return;
      }
      await navigator.share({ files: [file], title: "qrody QR" });
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
      setToast(t("action.shareFailed"));
    }
  };

  const onPrint = async () => {
    const blob = await fetchCurrentBlob();
    if (!blob || typeof document === "undefined") return;
    const url = URL.createObjectURL(blob);
    const root = document.getElementById("qr-print-root");
    const target = document.getElementById("qr-print-target");
    if (!root || !(target instanceof HTMLImageElement)) {
      URL.revokeObjectURL(url);
      return;
    }
    target.src = url;
    target.style.width = `${displayMm}mm`;
    target.style.height = `${displayMm}mm`;
    const cleanup = () => {
      URL.revokeObjectURL(url);
      target.removeAttribute("src");
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    window.setTimeout(() => window.print(), 50);
  };

  return { toast, mounted, canShareFiles, onCopyImage, onCopyHtml, onShare, onPrint };
}
